import React, { useState } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, FileText, Power } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../ui/dialog';
import { RichTextEditor } from '../../ui/rich-text-editor';
import { toast } from 'sonner';
import { sanitizeHtml } from '../../../lib/sanitize';
import { PLACEHOLDERS } from '../../../lib/emailTemplates';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const EMPTY = { name: '', category: 'General', subject: '', body_html: '', active: true };

export const ReplyTemplatesPanel = ({ token, templates, onChanged }) => {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const openNew = () => { setEditing('new'); setForm(EMPTY); };
  const openEdit = (t) => { setEditing(t); setForm({ name: t.name, category: t.category || 'General', subject: t.subject || '', body_html: t.body_html || '', active: t.active !== false }); };

  const save = async () => {
    if (!form.name.trim() || !form.subject.trim()) return toast.error('Name and subject are required');
    setSaving(true);
    try {
      const body = { ...form, kind: 'reply' };
      if (editing === 'new') await axios.post(`${API_URL}/admin/email-templates`, body, { headers });
      else await axios.put(`${API_URL}/admin/email-templates/${editing.id}`, body, { headers });
      toast.success(editing === 'new' ? 'Template created' : 'Template updated');
      setEditing(null); onChanged();
    } catch { toast.error('Could not save the template'); } finally { setSaving(false); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await axios.delete(`${API_URL}/admin/email-templates/${t.id}`, { headers }); toast.success('Template deleted'); onChanged(); } catch { toast.error('Could not delete the template'); }
  };
  const toggle = async (t) => {
    try { await axios.put(`${API_URL}/admin/email-templates/${t.id}`, { active: t.active === false }, { headers }); onChanged(); } catch { toast.error('Could not update the template'); }
  };

  const groups = templates.reduce((acc, t) => { (acc[t.category || 'General'] ||= []).push(t); return acc; }, {});

  return (
    <div className="space-y-4" data-testid="reply-templates-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold">Reply templates</h3>
          <p className="text-xs text-neutral-500">One-click answers for the chat reply and email composer. Placeholders: {PLACEHOLDERS.map((p) => <code key={p.key} className="mx-0.5 px-1 rounded bg-neutral-100 text-[10px]">{`{{${p.key}}}`}</code>)}</p>
        </div>
        <Button size="sm" onClick={openNew} className="h-8 text-xs bg-amber-600 hover:bg-amber-700" data-testid="reply-template-add"><Plus className="w-3.5 h-3.5 mr-1" />New template</Button>
      </div>
      {templates.length === 0 && <p className="text-xs text-neutral-500 border border-dashed rounded-lg p-6 text-center">No reply templates yet.</p>}
      {Object.entries(groups).map(([cat, items]) => (
        <section key={cat}>
          <h4 className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 mb-2">{cat}</h4>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {items.map((t) => (
              <article key={t.id} className={`bg-white border rounded-xl p-3 flex flex-col gap-2 ${t.active === false ? 'opacity-60' : ''}`} data-testid={`reply-template-${t.id}`}>
                <div className="flex items-start gap-2">
                  <FileText className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{t.name}</p>
                    <p className="text-[11px] text-neutral-500 truncate">{t.subject}</p>
                  </div>
                  <button type="button" onClick={() => toggle(t)} className={`p-1 rounded ${t.active === false ? 'text-neutral-400 hover:bg-emerald-50' : 'text-emerald-600 hover:bg-amber-50'}`} title={t.active === false ? 'Enable' : 'Disable'} data-testid={`reply-template-toggle-${t.id}`}><Power className="w-3.5 h-3.5" /></button>
                  <button type="button" onClick={() => openEdit(t)} className="p-1 rounded hover:bg-neutral-100 text-neutral-500" title="Edit" data-testid={`reply-template-edit-${t.id}`}><Edit className="w-3.5 h-3.5" /></button>
                  <button type="button" onClick={() => remove(t)} className="p-1 rounded hover:bg-red-50 text-neutral-500 hover:text-red-600" title="Delete" data-testid={`reply-template-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                <div className="text-[11px] text-neutral-600 line-clamp-3 [&_p]:my-0.5" dangerouslySetInnerHTML={{ __html: sanitizeHtml(t.body_html || '') }} />
              </article>
            ))}
          </div>
        </section>
      ))}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl" data-testid="reply-template-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">{editing === 'new' ? 'New reply template' : 'Edit reply template'}</DialogTitle>
            <DialogDescription className="text-xs">Use {'{{customer_name}}'}, {'{{company}}'} and {'{{agent_name}}'} — they're filled in when the template is inserted.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="sm:col-span-2"><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Quote sent" className="h-9 text-sm" data-testid="reply-template-name" /></div>
            <div><Label className="text-xs">Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} list="reply-template-categories" className="h-9 text-sm" data-testid="reply-template-category" /><datalist id="reply-template-categories">{['General', 'Follow-up', 'Sales', 'Scheduling', 'Samples', 'Support'].map((c) => <option key={c} value={c} />)}</datalist></div>
            <div className="sm:col-span-3"><Label className="text-xs">Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="h-9 text-sm" data-testid="reply-template-subject" /></div>
            <div className="sm:col-span-3"><Label className="text-xs">Body</Label><div data-testid="reply-template-body"><RichTextEditor value={form.body_html} onChange={(v) => setForm({ ...form, body_html: v })} placeholder="Hi {{customer_name}}, …" className="[&_.ProseMirror]:min-h-[160px] [&_.ProseMirror]:text-sm" /></div></div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEditing(null)}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="reply-template-save">{editing === 'new' ? 'Create' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReplyTemplatesPanel;
